/**
 * The figures the filing does not vouch for, each with the smallest question that settles it.
 *
 * From the tab audit (v2_figure_status), already ordered on the server: a statement whose
 * printed arithmetic does not close first - it is every figure on it - then largest gap
 * first. The same figures are marked in their own cells on their own tabs (V2Table).
 *
 * A figure the user corrects in its cell becomes a lock like any edit. One they look at and
 * find right as it stands is confirmed here, kept on the inputs, and not asked again.
 */

import { useState } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { FigureStatus } from '../../../types/financial-model-v2';
import { formatMoney } from '../../../lib/currency';

const SHOWN = 5;

/** What the panel lists: what is unverified or fails a check, never what is vouched for. */
export function outstandingFigures(figures: FigureStatus[] | undefined): FigureStatus[] {
  return (figures ?? []).filter((f) => f.status === 'unverified' || f.status === 'failing');
}

export function FiguresToCheckPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const audit = useFinancialModelV2Store((s) => s.tabAudit);
  const confirmFigure = useFinancialModelV2Store((s) => s.confirmFigure);
  const [expanded, setExpanded] = useState(false);

  const figures = outstandingFigures(audit?.figures);
  if (!model || figures.length === 0) return null;
  const currency = model.settings.baseCurrency;
  const shown = expanded ? figures : figures.slice(0, SHOWN);
  const hidden = figures.length - SHOWN;

  return (
    <section
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
      data-testid="figures-to-check"
    >
      <h3 className="text-sm font-semibold text-gray-900">
        Figures the filing does not vouch for
        <span className="ml-1 font-normal text-gray-600">({figures.length})</span>
      </h3>
      <p className="mt-1 text-xs text-gray-600">
        Every other historical figure is the filing's own, proved by the totals it prints.
        These are marked in their cells; correct one there, or confirm it here if it is right.
      </p>
      <ul className="mt-2 divide-y divide-black/5">
        {shown.map((figure) => (
          <li key={`${figure.key}-${figure.field}-${figure.period}`} className="py-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-gray-900">
                {figure.tab}: {figure.line || 'a line'}
                {figure.year ? ` (${figure.year})` : ''}
              </span>
              <span className="flex shrink-0 items-baseline gap-3">
                {figure.gap !== 0 && (
                  <span className="font-mono text-sm tabular-nums text-amber-800">
                    {formatMoney(figure.gap, currency)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void confirmFigure(figure.key)}
                  className="text-xs font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
                >
                  Looks right
                </button>
              </span>
            </div>
            <div className="mt-0.5 text-xs text-gray-600">{figure.question}</div>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="mt-1 text-xs font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
        >
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </section>
  );
}
